import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ShoppingCart } from 'lucide-react';
import { brand } from '../mock';
import { useCart } from '../context/CartContext';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Marketplaces', to: '/marketplaces' },
  { label: 'Testimonials', to: '/testimonials' },
  { label: 'Contact', to: '/contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, setDrawerOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-white/90'
      }`}
    >
      <div className="container-x flex items-center justify-between h-[72px] gap-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-green)] flex items-center justify-center text-white font-black text-lg shadow-md">
            EC
          </div>
          <div className="leading-tight">
            <div className="font-black text-[14px] text-[color:var(--ink)] tracking-tight whitespace-nowrap">
              EC SAFETRADE
            </div>
            <div className="text-[9px] font-semibold text-[color:var(--brand-orange)] tracking-[2px]">
              SOLUTIONS
            </div>
          </div>
        </Link>

        {/* Desktop nav — only visible at xl (1280px+) */}
        <nav className="hidden xl:flex items-center gap-6 flex-1 justify-center">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                `nav-link whitespace-nowrap ${isActive ? 'text-[color:var(--brand-green)] after:!w-full' : ''}`
              }
              style={({ isActive }) => (isActive ? { color: 'var(--brand-green)' } : {})}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right-side actions — full at xl, compact at lg, hidden below lg */}
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          {/* Phone — only at 2xl+ to avoid crowding */}
          <a
            href={`tel:${brand.phoneRaw}`}
            className="hidden 2xl:flex items-center gap-2 text-sm font-semibold text-[color:var(--ink)] hover:text-[color:var(--brand-green)] transition-colors whitespace-nowrap mr-2"
          >
            <Phone size={16} />
            {brand.phone}
          </a>

          {/* Cart */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="relative w-10 h-10 rounded-full bg-[color:var(--brand-green-light)] text-[color:var(--brand-green-dark)] flex items-center justify-center hover:bg-[color:var(--brand-green)] hover:text-white transition shrink-0"
            aria-label="Open cart"
          >
            <ShoppingCart size={18} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[color:var(--brand-orange)] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {count}
              </span>
            )}
          </button>

          {/* Connect With Us — orange CTA */}
          <a
            href="https://connectus.ecsafetradesolution.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition whitespace-nowrap"
            style={{ background: 'var(--brand-orange)' }}
          >
            Connect With Us
          </a>

          {/* Get Started */}
          <Link to="/contact" className="btn-primary text-sm whitespace-nowrap">
            Get Started
          </Link>
        </div>

        {/* Mobile / tablet hamburger — visible below lg */}
        <button
          className="lg:hidden text-[color:var(--ink)] shrink-0"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile / tablet menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-neutral-100 shadow-lg">
          <div className="container-x py-4 flex flex-col gap-3">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) =>
                  `py-2 font-medium ${isActive ? 'text-[color:var(--brand-green)]' : 'text-[color:var(--ink)]'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex flex-wrap gap-2 pt-2">
              <a
                href="https://connectus.ecsafetradesolution.com/"
                onClick={() => setOpen(false)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-md"
                style={{ background: 'var(--brand-orange)' }}
              >
                Connect With Us
              </a>
              <Link to="/contact" className="btn-primary text-sm">Get Started</Link>
              <a
                href={`tel:${brand.phoneRaw}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border-2 border-[color:var(--brand-green)] text-[color:var(--brand-green)]"
              >
                <Phone size={14} /> {brand.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
